/**
 * Copyright (c) 2024-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql } from "@apollo/client";
import { CheckLine } from "@ndla/icons";
import { Text } from "@ndla/primitives";
import { styled } from "@ndla/styled-system/jsx";
import { linkOverlay } from "@ndla/styled-system/patterns";
import { ArticleByline } from "@ndla/ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import config from "../../config";
import type { GQLLearningpathMenu_LearningpathFragment } from "../../graphqlTypes";
import { routes, toLearningPath } from "../../routeHelpers";
import { formatDate } from "../../util/formatDate";
import { FavoriteButton } from "../Article/FavoritesButton";
import { AuthContext } from "../AuthenticationContext";
import { AddResourceToFolderModal } from "../MyNdla/AddResourceToFolderModal";
import { Launchpad } from "../Resource/Launchpad";
import {
  CollapsedLinkComponent,
  StepperIndicator,
  StepperList,
  StepperListItem,
  StepperRoot,
  StepperSafeLink,
} from "../Stepper";
import { CopyLearningPath } from "./components/CopyLearningPath";
import type { LearningpathContext } from "./learningpathUtils";

interface Props {
  resourcePath?: string | null;
  learningpath: GQLLearningpathMenu_LearningpathFragment | undefined | null;
  currentIndex: number | undefined;
  context?: LearningpathContext;
  displayContext: "mobile" | "desktop";
  loading: boolean;
  hasIntroduction: boolean;
}

const StyledStepperList = styled(StepperList, {
  base: {
    gap: "xxsmall",
  },
});

const stepLink = (
  learningpathId: number,
  stepId: number | undefined,
  resourcePath: string | undefined | null,
  context: LearningpathContext | undefined,
) => {
  return context === "preview"
    ? routes.myNdla.learningpathPreview(learningpathId, stepId)
    : toLearningPath(learningpathId, stepId, resourcePath);
};

const INTRODUCTION_ID = -1;

export const LearningpathMenu = ({
  resourcePath,
  learningpath,
  currentIndex,
  context = "default",
  hasIntroduction,
  displayContext,
  loading,
}: Props) => {
  const currentStep = currentIndex !== undefined ? learningpath?.learningsteps[currentIndex] : undefined;
  const currentId =
    learningpath && currentStep?.seqNo !== undefined
      ? currentStep.id
      : hasIntroduction && !currentIndex
        ? INTRODUCTION_ID
        : undefined;
  const [completed, setCompleted] = useState<number[]>(currentId ? [currentId] : []);
  const [prevCurrentId, setPrevCurrentId] = useState(currentId);
  const { t, i18n } = useTranslation();

  const indicatorOffset = hasIntroduction ? 2 : 1;
  const { user } = useContext(AuthContext);

  if (currentId !== prevCurrentId) {
    setPrevCurrentId(currentId);
    if (currentId && !completed.includes(currentId)) {
      setCompleted(completed.concat(currentId));
    }
  }

  return (
    <Launchpad
      type={t("contentTypes.learningpath")}
      ariaLabel={t("launchpad.learningpathLabel", { learningpath: learningpath?.title })}
      name={learningpath?.title ?? ""}
      context={displayContext}
      actions={
        learningpath && context === "default" ? (
          <>
            <AddResourceToFolderModal
              resource={{
                id: learningpath.id.toString(),
                path: resourcePath ?? toLearningPath(learningpath.id),
                resourceType: "learningpath",
              }}
            >
              <FavoriteButton path={resourcePath ?? toLearningPath(learningpath.id)} />
            </AddResourceToFolderModal>
            {user?.role === "employee" && <CopyLearningPath learningpath={learningpath} />}
          </>
        ) : null
      }
      loading={loading}
    >
      {(collapsed) =>
        !learningpath ? undefined : (
          <>
            <StepperRoot
              aria-label={t("learningpathPage.learningsteps")}
              line
              aria-hidden={collapsed}
              collapsed={collapsed}
            >
              <StyledStepperList>
                {!!hasIntroduction && (
                  <StepperListItem
                    completed={completed.includes(INTRODUCTION_ID)}
                    // for collapsed styling
                    data-current={currentIndex === undefined ? "" : undefined}
                  >
                    <CollapsedLinkComponent
                      to={stepLink(learningpath.id, undefined, resourcePath, context)}
                      collapsed={collapsed}
                      aria-current={currentIndex === undefined ? "page" : undefined}
                      css={linkOverlay.raw()}
                      aria-label={`${t("learningpathPage.introduction")}${completed.includes(INTRODUCTION_ID) ? `. ${t("learningpathPage.stepCompleted")}` : ""}`}
                      title={`${t("learningpathPage.introduction")}${completed.includes(INTRODUCTION_ID) ? `. ${t("learningpathPage.stepCompleted")}` : ""}`}
                    >
                      <StepperIndicator>
                        {completed.includes(INTRODUCTION_ID) && currentIndex !== undefined ? (
                          <CheckLine size="small" />
                        ) : (
                          1
                        )}
                      </StepperIndicator>
                    </CollapsedLinkComponent>
                    {!collapsed && (
                      <StepperSafeLink
                        to={stepLink(learningpath.id, undefined, resourcePath, context)}
                        aria-current={currentIndex === undefined ? "page" : undefined}
                        css={linkOverlay.raw()}
                        aria-label={`${t("learningpathPage.introduction")}${completed.includes(INTRODUCTION_ID) ? `. ${t("learningpathPage.stepCompleted")}` : ""}`}
                      >
                        {t("learningpathPage.introduction")}
                      </StepperSafeLink>
                    )}
                  </StepperListItem>
                )}
                {learningpath.learningsteps.map((step, index) => (
                  <StepperListItem
                    key={step.id}
                    completed={completed.includes(step.id)}
                    // for collapsed styling
                    data-current={currentIndex === index ? "" : undefined}
                  >
                    <CollapsedLinkComponent
                      to={stepLink(learningpath.id, step.id, resourcePath, context)}
                      collapsed={collapsed}
                      aria-current={currentIndex === index ? "page" : undefined}
                      css={linkOverlay.raw()}
                      aria-label={`${step.title}${completed.includes(step.id) ? `. ${t("learningpathPage.stepCompleted")}` : ""}`}
                      title={`${step.title}${completed.includes(step.id) ? `. ${t("learningpathPage.stepCompleted")}` : ""}`}
                    >
                      <StepperIndicator>
                        {completed.includes(step.id) && index !== currentIndex ? (
                          <CheckLine size="small" />
                        ) : (
                          index + indicatorOffset
                        )}
                      </StepperIndicator>
                    </CollapsedLinkComponent>

                    {!collapsed && (
                      <StepperSafeLink
                        to={stepLink(learningpath.id, step.id, resourcePath, context)}
                        css={linkOverlay.raw()}
                        aria-current={currentIndex === index ? "page" : undefined}
                        aria-label={`${step.title}${completed.includes(step.id) ? `. ${t("learningpathPage.stepCompleted")}` : ""}`}
                      >
                        {step.title}
                      </StepperSafeLink>
                    )}
                  </StepperListItem>
                ))}
              </StyledStepperList>
            </StepperRoot>
            {!collapsed && (
              <ArticleByline
                authors={learningpath.copyright.contributors}
                published={formatDate(learningpath.lastUpdated, i18n.language)}
                bylineType="learningPath"
                bylineSuffix={learningpath.isMyNDLAOwner ? <Text>{t("learningpathPage.bylineSuffix")}</Text> : null}
                learningpathCopiedFrom={
                  learningpath.basedOn ? config.ndlaFrontendDomain + learningpath.basedOn : undefined
                }
              />
            )}
          </>
        )
      }
    </Launchpad>
  );
};

LearningpathMenu.fragments = {
  learningpath: gql`
    fragment LearningpathMenu_Learningpath on BaseLearningpath {
      id
      title
      lastUpdated
      copyright {
        contributors {
          type
          name
        }
      }
      learningsteps {
        id
        title
        seqNo
      }
      basedOn
      isMyNDLAOwner
      ...CopyLearningpath_Learningpath
    }
    ${CopyLearningPath.fragments.learningpath}
  `,
};
